import { ReactNode, type KeyboardEvent, type MouseEvent, useCallback, useState } from 'react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { GroupParticipant, getGroupParticipantFirstName } from '@frontend/thread-header-context';
import { ListRow, NakedButton, styles, useTooltip } from '@frontend/design-system';
import {
  compactModeStyles,
  contactsPaneButtonStyles,
  containerStyles,
  expandedContainerStyles,
  editButtonStyles,
  editInputBoldStyles,
  editInputStyles,
  editTitleTriggerStyles,
  hiddenNamesTitleStyles,
  hiddenNamesWrapperStyles,
  namingWrapperStyles,
  tooltipContentStyles,
  visibleNamesTitleStyles,
} from './group-thread-title.styles';
import { truncatedNames } from './utils/truncated-names';

type GroupThreadTitleProps = {
  participants: GroupParticipant[];
  isBold?: boolean;
  leadingIcon?: ReactNode;
  /** Slot rendered after the group name (e.g. error status icon). AI statuses should not be passed here. */
  trailingIcon?: ReactNode;
  /** Slot rendered between the group name and the contacts pane button (e.g. opt-out indicator) */
  optOutIndicator?: ReactNode;
  isCompactMode?: boolean;
  showContactsPaneButton?: boolean;
  onGroupIconClick?: () => void;
  isEditable?: boolean;
  groupName?: string;
  onGroupNameSave?: (name?: string) => void;
  titleMaxWidth?: string | number;
  noTrailingContent?: boolean;
};

type HiddenNamesOverflowProps = {
  hiddenNames: string[];
  hiddenNamesTriggerProps: ReturnType<typeof useTooltip>['triggerProps'];
  isBold?: boolean;
  isCompactMode?: boolean;
};

const HiddenNamesOverflow = ({
  hiddenNames,
  hiddenNamesTriggerProps,
  isBold,
  isCompactMode,
}: HiddenNamesOverflowProps) => {
  const { t } = useTranslation('thread-header');

  return (
    <span css={hiddenNamesWrapperStyles}>
      <ListRow.Content.Title
        css={[hiddenNamesTitleStyles, isCompactMode && compactModeStyles]}
        isBold={isBold}
        isCompactMode={isCompactMode}
      >
        {', '}
        <span
          tabIndex={0}
          aria-label={t('Show hidden participant names')}
          title={hiddenNames.join(', ')}
          {...hiddenNamesTriggerProps}
        >
          {t('{{count}} Other', { count: hiddenNames.length })}
        </span>
      </ListRow.Content.Title>
    </span>
  );
};

export const GroupThreadTitle = ({
  participants,
  isBold,
  leadingIcon,
  trailingIcon,
  optOutIndicator,
  isCompactMode,
  showContactsPaneButton = false,
  onGroupIconClick,
  isEditable = false,
  groupName,
  onGroupNameSave,
  titleMaxWidth,
  noTrailingContent,
}: GroupThreadTitleProps) => {
  const { t } = useTranslation('thread-header');
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');

  const displayNames = participants.map((participant) => getGroupParticipantFirstName(participant)).filter(Boolean);
  const { visibleNames, hiddenNames, shouldShowHiddenNames: hasHiddenNames } = truncatedNames(displayNames);

  const {
    Tooltip,
    triggerProps: hiddenNamesTriggerProps,
    tooltipProps: hiddenNamesTooltipProps,
  } = useTooltip({
    trigger: 'hover',
    placement: 'top-start',
    offset: 8,
  });

  const {
    Tooltip: EditTooltip,
    triggerProps: editTriggerProps,
    tooltipProps: editTooltipProps,
  } = useTooltip({
    trigger: 'hover',
    placement: 'top',
    offset: 8,
  });

  const trimmedGroupName = groupName?.trim();
  const hasGroupName = !!trimmedGroupName;
  const displayTitle = hasGroupName ? trimmedGroupName : visibleNames.join(', ');
  const shouldShowHiddenNames = !hasGroupName && hasHiddenNames;

  const handleInputRef = useCallback((element: HTMLInputElement | null) => {
    if (!element) return;

    const length = element.value.length;
    element.focus();
    element.setSelectionRange(length, length);
  }, []);

  const handleBeginEdit = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setEditValue(
      hasGroupName
        ? trimmedGroupName
        : [...visibleNames, ...(hasHiddenNames ? [t('{{count}} Other', { count: hiddenNames.length })] : [])].join(', ')
    );
    setIsEditing(true);
  };

  const handleCommitSave = () => {
    const nextName = editValue.trim();
    onGroupNameSave?.(nextName || undefined);
    setIsEditing(false);
  };

  const handleEditKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.blur();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setIsEditing(false);
    }
  };

  const handleCountClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onGroupIconClick?.();
  };

  return (
    <>
      <div
        css={[containerStyles, noTrailingContent && expandedContainerStyles]}
        onClick={isEditable ? (e) => e.stopPropagation() : undefined}
      >
        {leadingIcon}
        <div css={[namingWrapperStyles, !!titleMaxWidth && { maxWidth: titleMaxWidth, width: 'fit-content' }]}>
          {isEditing && (
            <input
              ref={handleInputRef}
              value={editValue}
              onChange={(event) => setEditValue(event.target.value)}
              onKeyDown={handleEditKeyDown}
              onBlur={handleCommitSave}
              aria-label={t('Edit Group Chat Name')}
              data-trackingid='group-thread-title-edit-input'
              css={[editInputStyles, isCompactMode && compactModeStyles, isBold && editInputBoldStyles]}
            />
          )}

          {!isEditing && isEditable && (
            <>
              <NakedButton
                onClick={handleBeginEdit}
                aria-label={t('Edit Group Chat Name')}
                data-trackingid='group-thread-title-edit-button'
                css={editButtonStyles}
              >
                <span css={editTitleTriggerStyles} {...editTriggerProps}>
                  <ListRow.Content.Title
                    css={[styles.truncate, visibleNamesTitleStyles, isCompactMode && compactModeStyles]}
                    isBold={isBold}
                    isCompactMode={isCompactMode}
                  >
                    {displayTitle}
                  </ListRow.Content.Title>
                </span>
                {shouldShowHiddenNames && (
                  <HiddenNamesOverflow
                    hiddenNames={hiddenNames}
                    hiddenNamesTriggerProps={hiddenNamesTriggerProps}
                    isBold={isBold}
                    isCompactMode={isCompactMode}
                  />
                )}
              </NakedButton>
              <EditTooltip {...editTooltipProps}>{t('Edit Group Chat Name')}</EditTooltip>
            </>
          )}

          {!isEditing && !isEditable && (
            <>
              <ListRow.Content.Title
                css={[styles.truncate, visibleNamesTitleStyles, isCompactMode && compactModeStyles]}
                isBold={isBold}
                isCompactMode={isCompactMode}
              >
                {displayTitle}
              </ListRow.Content.Title>
              {shouldShowHiddenNames && (
                <HiddenNamesOverflow
                  hiddenNames={hiddenNames}
                  hiddenNamesTriggerProps={hiddenNamesTriggerProps}
                  isBold={isBold}
                  isCompactMode={isCompactMode}
                />
              )}
            </>
          )}
        </div>
        {trailingIcon}
        {optOutIndicator}
        {showContactsPaneButton && (
          <NakedButton
            onClick={handleCountClick}
            aria-label={t('Open group participants panel')}
            data-trackingid='group-thread-title-contact-count'
            css={contactsPaneButtonStyles}
          >
            <Icon name='contacts' size={14} />
            <span>{displayNames.length}</span>
          </NakedButton>
        )}
      </div>
      {shouldShowHiddenNames && (
        <Tooltip {...hiddenNamesTooltipProps}>
          <div css={tooltipContentStyles}>
            {hiddenNames.map((hiddenName, index) => (
              <span key={`${hiddenName}-${index}`}>{hiddenName}</span>
            ))}
          </div>
        </Tooltip>
      )}
    </>
  );
};
